import React from 'react';
import COLORS from 'constants/colors';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { Row, Col, Text, Separator } from '@cuemath/comps';
import ReadMoreButton from 'comps/read-more-button';

const DEMO_STATUS = {
  NOT_DONE: 'NOT_DONE',
};

const StyledCol = styled(Col)`
  @media (min-width: 769px) {
    border-right: 1px solid ${COLORS.GALLERY};
  }
`;

const LinkNav = styled(Link)`
  text-decoration: none;
  cursor: pointer;
  color: blue;
  text-align: center;
  &:hover {
    color: blue;
  }
  &:active {
    color: blue;
  }
`;

function getConductedOnString(timestamp) {
  if (!timestamp) return null;

  const currentTime = new Date(timestamp * 1000);
  const currentOffset = currentTime.getTimezoneOffset();
  const ISTOffset = 330; // IST offset UTC +5:30
  const ISTTime = new Date(currentTime.getTime() + (ISTOffset + currentOffset) * 60000);

  return `${format(ISTTime, 'Do MMM YYYY HH:mm a')} IST`;
}

const LabelValue = ({ label, showLink, showColon, topsideValue, value }) => {
  if (!value) {
    return null;
  }

  function val() {
    if (label === 'Teacher short bio') return <ReadMoreButton content={value} />;
    if (label === 'At Cuemath') return `since ${value}`;
    return value;
  }

  return (
    <Row alignItems="center" justifyContent={topsideValue && 'center'} gap={4}>
      <Text color={COLORS.COLA_LIGHT}>
        {label}
        {showColon && ':'}&nbsp;
      </Text>

      {showLink && (
        <LinkNav
          target="_blank"
          to={{
            pathname: `${showLink}`,
          }}
        >
          <Text color={COLORS.SKY}>{value}</Text>
        </LinkNav>
      )}

      {!showLink && (
        <Text
          color={COLORS.COLA}
          size={topsideValue && 'medium'}
          weight={topsideValue && 'bold'}
        >
          {val()}
        </Text>
      )}
    </Row>
  );
};

LabelValue.propTypes = {
  label: PropTypes.string.isRequired,
  showColon: PropTypes.bool,
  showLink: PropTypes.string,
  topsideValue: PropTypes.bool,
  value: PropTypes.string,
};

LabelValue.defaultProps = {
  showColon: true,
  showLink: null,
  topsideValue: false,
  value: null,
};

const StudentInfo = ({
  demoType,
  parentDemoPreferences,
  region,
  student,
  studentClassroom,
  studentId,
  teacher,
  teacherId,
}) => {
  const {
    device = '',
    grade,
    internet_strength: internetStrength = '',
    name,
    parent_name: parentName,
    phone,
  } = student || {};

  const { name: teacherName, no_of_classes: noOfClasses, phone: teacherPhone, profile } =
    teacher || {};

  const {
    cuemath_association_since: cuemathAssociationSince = null,
    education,
    short_bio: shortBio = null,
    work_experience: workExperience,
  } = profile || {};

  const {
    class_start_ts: classStartedOn,
    class_end_ts: classEndedOn,
    status,
    teacher_classroom_feedback: teacherClassroomFeedback,
    teacher_classroom_id: teacherClassroomId,
  } = studentClassroom || {};

  const { parent_reaction: parentReaction } = teacherClassroomFeedback || {};
  const timeDiff = (classEndedOn - classStartedOn) / 60;
  const isDemoNotDone =
    status === 'A' ||
    status === 'TA' ||
    timeDiff < 20 ||
    parentReaction === DEMO_STATUS.NOT_DONE;

  const RenderParentsExpectations = parentExpectations => {
    return parentExpectations.map((parentsExpectationData, index) => {
      return <li key={index}>{parentsExpectationData}</li>;
    });
  };

  const parentDemoPreferencesLength =
    parentDemoPreferences &&
    Array.isArray(parentDemoPreferences) &&
    parentDemoPreferences.length;

  const teachersData = [
    { label: 'Teacher Name', value: teacherName },
    { label: 'Contact number', value: teacherPhone },
    {
      label: 'Educational Qualifications',
      value: education,
    },
    {
      label: 'At Cuemath',
      value: cuemathAssociationSince,
    },
    { label: 'Number of class at Cuemath', value: noOfClasses },
    {
      label: 'Work Experience',
      value: workExperience,
    },
    {
      label: 'Teacher short bio',
      value: shortBio,
    },
  ];

  return (
    <Row>
      <Row alignItems="center">
        <Col sm={3} xs={12} gutter={58} gap={4}>
          <LabelValue label="Demo Type" value={demoType} topsideValue={true} />
        </Col>
        <Col sm={3} xs={12} gutter={32} gap={4}>
          <LabelValue
            label="Demo Status"
            value={isDemoNotDone ? `Demo not done` : `Demo done, ${timeDiff} mins`}
            topsideValue={true}
          />
        </Col>
        <Col sm={6} xs={12} gutter={68} gap={4}>
          <LabelValue
            label="Demo conducted on"
            value={getConductedOnString(classStartedOn)}
            topsideValue={true}
          />
        </Col>
      </Row>

      <Row alignItems="flex-start">
        <StyledCol sm={3} xs={12} gutter={18} gap={4}>
          <LabelValue
            label="Child Name"
            value={name}
            showLink={`/student-form/${studentId}/`}
          />
          <LabelValue label="Grade" value={grade} />
          <LabelValue label="Region" value={region} />
          <LabelValue label="Device" value={device} />
          <LabelValue label="Internet" value={internetStrength} />
          <LabelValue
            label="AV Logs"
            showColon={false}
            value={`(view logs)`}
            showLink={`/users/teacher-classroom/${teacherClassroomId}?sales=True`}
          />
          <Separator height={8} transparent />
        </StyledCol>
        <StyledCol sm={3} xs={12} gutter={18} gap={4}>
          <LabelValue label="Parent Name" value={parentName} />
          <LabelValue label="Parent number" value={phone} />
          <LabelValue
            label="Parent Expectations"
            value={parentDemoPreferencesLength === 0 && 'None'}
          />
          {parentDemoPreferencesLength > 0 &&
            RenderParentsExpectations(parentDemoPreferences)}
          <Separator height={8} transparent />
        </StyledCol>
        <Col sm={6} xs={12} gutter={18} gap={4}>
          {teachersData.map(({ label, value }) => {
            return (
              <LabelValue
                label={label}
                value={value}
                showLink={label === 'Teacher Name' && `/users/teachers/${teacherId}/`}
              />
            );
          })}
        </Col>
      </Row>
    </Row>
  );
};

StudentInfo.propTypes = {
  demoType: PropTypes.string,
  parentDemoPreferences: PropTypes.array,
  region: PropTypes.string,
  student: PropTypes.shape({}),
  studentClassroom: PropTypes.shape({}),
  studentId: PropTypes.string,
  teacher: PropTypes.shape({}),
  teacherId: PropTypes.string,
};

StudentInfo.defaultProps = {
  demoType: null,
  parentDemoPreferences: null,
  region: null,
  student: null,
  studentClassroom: null,
  studentId: null,
  teacher: null,
  teacherId: null,
};

export default StudentInfo;
